import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import useLogger from '@/services/loggerService'

import useBlProviders from '@/composables/providers/useBlProvider'
import DevicesDt from '@/services/dataTransferModules/devicesDt'

import useSensorTypes from '@/composables/staticData/useSensorTypes'
import { getDeviceSensorWithAdditionalAttributes } from '@/helpers/entities/devices/devicesHelper'

export default function useDevices() {
	const devicesDt = new DevicesDt()
	const { t: $t } = useI18n()
	const logger = useLogger()
	const { sensorTypesObj } = useSensorTypes()

	const postProcessEntityCallback = (device) => {
		device.sensors = (device.sensors ?? []).map((sensor) =>
			getDeviceSensorWithAdditionalAttributes(sensor, sensorTypesObj.value)
		)
		const hashedSensors = (device.sensors ?? []).reduce((sensorAcc, sensorObj) => {
			sensorAcc[sensorObj.id] = sensorObj
			return sensorAcc
		}, {})
		device.__ao__hashed_sensors = hashedSensors
		return device
	}

	const {
		selectedCompanyId,

		loadEntityFlags: flags,

		entities: fullDevices,
		getEntities: getFulledDevices,

		extendedEntities: extendedDevices,
		getExtendedEntities: getExtendedDevices,

		basedEntities: baseDevices,
		getBasedEntities: getBasedDevices,

		hashedExtendedEntities: hashedExtendedDevices,
		getHashedExtendedEntities: getHashedExtendedDevices,

		entity: device,
		getEntity: getDevice,

		entityPopup: popupDevice,
		getEntityPopup: getPopupDevice,

		entityPreview: previewDevice,
		getEntityPreview: getPreviewDevice,

		patchEntity: patchDevice,
		updateEntity: putDevice,
	} = useBlProviders(
		devicesDt,
		Object.assign({
			postProcessEntityCallback,

			showErrorNotificationForLoadingAllEntities: true,

			showErrorNotificationForCreatingEntity: true,

			showErrorNotificationForLoadingOneEntity: true,

			showErrorNotificationForUpdatingEntity: true,

			showErrorNotificationForDeletingEntity: true,

			additionalLoadingFlags: {
				isLoadingParams: false,
				isLoadingRawGpsParams: false,
			},
		})
	)

	const deviceRawGpsParams = ref([])
	const deviceParams = ref(null)

	const getDeviceRawGpsParams = (deviceId) => {
		return new Promise((resolve, reject) => {
			flags.isLoadingRawGpsParams = true

			devicesDt
				.getDeviceRawGpsParams(selectedCompanyId.value, deviceId)
				.then((res) => {
					deviceRawGpsParams.value = res
					flags.isLoadingRawGpsParams = false

					resolve(deviceRawGpsParams.value)
				})
				.catch(() => {
					flags.isLoadingRawGpsParams = false

					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Device loading error'),
					})

					reject()
				})
		})
	}
	const getDeviceParams = (deviceId) => {
		return new Promise((resolve, reject) => {
			flags.isLoadingParams = true

			devicesDt
				.getDeviceParams(selectedCompanyId.value, deviceId)
				.then((res) => {
					deviceParams.value = res
					flags.isLoadingParams = false

					resolve(deviceParams.value)
				})
				.catch(() => {
					flags.isLoadingParams = false

					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Device loading error'),
					})

					reject()
				})
		})
	}

	return {
		flags,
		fullDevices,
		extendedDevices,
		baseDevices,
		previewDevice,
		popupDevice,
		device,
		deviceRawGpsParams,
		deviceParams,

		getBasedDevices,
		getExtendedDevices,
		getFulledDevices,
		getDevice,
		getPreviewDevice,
		getPopupDevice,
		putDevice,
		patchDevice,
		getDeviceRawGpsParams,
		getDeviceParams,

		hashedExtendedDevices,
		getHashedExtendedDevices,
	}
}
